import { PORTALS_Z_INDEX, BLOCKING_LAYER_BG_Z_INDEX } from '@wix/thunderbolt-commons'
import { withDependencies } from '@wix/thunderbolt-ioc'
import {
	ComponentsStylesOverridesSymbol,
	IComponentsStylesOverrides,
	IStructureAPI,
	StructureAPI,
} from '@wix/thunderbolt-symbols'
import { IModelUpdatesHandlersImplementor } from 'ds-feature-model-updates-invoker'
import { name } from './symbols'
import type { BlockingLayerHandlers } from './types'

export const BLOCKING_LAYER_ID = 'BLOCKING_LAYER'
export const BLOCKING_LAYER_BG_ID = 'BLOCKING_LAYER_BACKGROUND'
export const BLOCKING_LAYER_MAX_Z_INDEX = PORTALS_Z_INDEX
export const SITE_HEADER_ID = 'SITE_HEADER'
export const SITE_FOOTER_ID = 'SITE_FOOTER'

export const BlockingLayer = withDependencies(
	[StructureAPI, ComponentsStylesOverridesSymbol],
	(
		structureApi: IStructureAPI,
		componentsStylesOverrides: IComponentsStylesOverrides
	): IModelUpdatesHandlersImplementor<BlockingLayerHandlers> => {
		const bgZIndex = BLOCKING_LAYER_BG_Z_INDEX
		const blockLayerStyles = {
			display: 'inline-block',
			width: '100vw',
			height: '100vh',
			position: 'fixed',
			top: '0',
			left: '0',
			'z-index': BLOCKING_LAYER_MAX_Z_INDEX,
		}

		const zIndexOverride = {
			'z-index': 'auto !important',
		}
		const zIndexReset = {
			'z-index': null,
		}
		return {
			featureName: name,
			handlers: {
				showOrHideBlockingLayer: async ({ shouldShowBlockingLayer, backgroundStyles }) => {
					// If should not show the blocking layer we remove the component from
					// the structure if it exists, otherwise we add the blocking layer to the structure
					if (!shouldShowBlockingLayer && structureApi.get(BLOCKING_LAYER_ID)) {
						structureApi.removeComponentFromDynamicStructure(BLOCKING_LAYER_ID)
						structureApi.removeComponentFromDynamicStructure(BLOCKING_LAYER_BG_ID)
						// see comment regarding https://jira.wixpress.com/browse/TB-6761 below
						componentsStylesOverrides.update({
							[SITE_HEADER_ID]: zIndexReset,
							[SITE_FOOTER_ID]: zIndexReset,
						})
					} else if (shouldShowBlockingLayer && backgroundStyles) {
						// Apply the styles for the blocking layer
						componentsStylesOverrides.update({
							[BLOCKING_LAYER_ID]: blockLayerStyles,
							[BLOCKING_LAYER_BG_ID]: {
								...blockLayerStyles,
								...backgroundStyles,
								'background-color': backgroundStyles.backgroundColor,
								opacity: backgroundStyles.opacity?.toString(),
								'z-index': bgZIndex,
							},
							// for the setCompsToShowOnTop API to handle children of Header and Footer properly when they need to be on top of overlay we need to make sure that Header and Footer do not have a z-index lower than z-index of the blocking layer background https://jira.wixpress.com/browse/TB-6761
							[SITE_HEADER_ID]: zIndexOverride,
							[SITE_FOOTER_ID]: zIndexOverride,
						})

						// Add the blocking layer component to the dynamic structure if it's not already there
						!structureApi.isComponentInDynamicStructure(BLOCKING_LAYER_ID) &&
							(await structureApi.addComponentToDynamicStructure(BLOCKING_LAYER_ID, {
								componentType: 'DivWithChildren',
								components: [],
							}))
						!structureApi.isComponentInDynamicStructure(BLOCKING_LAYER_BG_ID) &&
							(await structureApi.addComponentToDynamicStructure(BLOCKING_LAYER_BG_ID, {
								componentType: 'DivWithChildren',
								components: [],
							}))
					}
				},
			},
		}
	}
)
